import { useMemo } from "react";
import SolidIconBuilder from "../../../shared/helpers/SolidIconBuilder";
import Icons from "../../../shared/constants/icon.constant";
import { Spinner } from "@/shared/components/ui/spinner";
import {
  useMembershipLevels,
  useMembershipStatus,
} from "../services/membership";

export default function MembershipLevel() {
  const {
    data: statusData,
    isLoading: loadingStatus,
    isError: statusError,
  } = useMembershipStatus();
  const {
    data: levels,
    isLoading: loadingLevels,
    isError: levelsError,
  } = useMembershipLevels();
  const baseCurrency = statusData?.plan?.currency || "USD";

  const currencyFormatter = useMemo(() => {
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: baseCurrency,
        maximumFractionDigits: 2,
      });
    } catch {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 2,
      });
    }
  }, [baseCurrency]);

  const formatAmount = (value?: number | null) => {
    if (value === undefined || value === null) return "—";
    return currencyFormatter.format(value);
  };

  const currentPlanName = statusData?.plan?.name ?? "Membership";
  // const currentStatus = statusData?.status ?? "UNKNOWN";
  const currentBilling = statusData?.plan?.billingType ?? "—";
  const currentAmount = formatAmount(statusData?.plan?.amount);

  const activeLevel = useMemo(() => {
    if (!levels || !levels.length) return null;
    const nameUpper = currentPlanName.toUpperCase();
    const found =
      levels.find(
        (lvl) =>
          lvl.name.toUpperCase() === nameUpper ||
          lvl.planCode?.toUpperCase() === nameUpper
      ) ?? levels[0];
    return found;
  }, [levels, currentPlanName]);

  const benefitGroups = useMemo(() => {
    if (!activeLevel) return [];
    const accessItems = activeLevel.config?.access ?? activeLevel.access;
    const groups: {
      title: string;
      items: { label: string; enabled: boolean }[];
    }[] = [];

    if (accessItems?.length) {
      groups.push({
        title: "Access",
        items: accessItems.map((label) => ({ label, enabled: true })),
      });
    }

    if (activeLevel.education?.length) {
      groups.push({
        title: "Education",
        items: activeLevel.education.map((label) => ({ label, enabled: true })),
      });
    }

    if (activeLevel.fees?.length) {
      groups.push({
        title: "Fees",
        items: activeLevel.fees.map((label) => ({ label, enabled: true })),
      });
    }

    if (Array.isArray(activeLevel.cost) && activeLevel.cost.length) {
      groups.push({
        title: "Cost",
        items: activeLevel.cost.map((label) => ({ label, enabled: true })),
      });
    } else if (typeof activeLevel.cost === "number") {
      groups.push({
        title: "Cost",
        items: [
          {
            label: currencyFormatter.format(activeLevel.cost),
            enabled: true,
          },
        ],
      });
    }

    return groups;
  }, [activeLevel, currencyFormatter]);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-xl md:text-2xl font-semibold">
          {currentPlanName} Membership
        </h1>
        {/* <p className="text-sm text-white/60">
          Status: {currentStatus} • Billing: {currentBilling} • Amount:{" "}
          {currentAmount}
        </p> */}
      </div>

      <div className="grid grid-cols-1 gap-4">
        <div className="relative rounded-xl overflow-hidden p-4 bg-[#13181d] h-[300px] w-[500px]">
          <img
            src="/assets/images/card-image.png"
            alt={currentPlanName}
            className="w-[500px] h-[300px]! absolute top-0 left-0 bottom-0 right-0 z-10"
          />
          <div className="flex items-center justify-between gap-4 z-20">
            <div className="flex items-center gap-3 z-20">
              <div className="flex flex-col z-20 absolute bottom-0 left-0 p-6">
                <span className="text-base md:text-lg font-semibold z-20">
                  {currentPlanName}
                </span>
                <span className="text-xs text-white/60 z-20">
                  Billing: {currentBilling}
                </span>
              </div>
            </div>
            <div className="flex flex-col items-end absolute bottom-0 right-0 z-20 p-6">
              <span className="text-base md:text-lg font-semibold">
                {currentAmount}
              </span>
              <span className="text-xs text-white/60">{baseCurrency}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold">Benefits</h2>
          {(loadingStatus || loadingLevels) && (
            <Spinner className="size-4 text-primary-300" />
          )}
        </div>

        {statusError || levelsError ? (
          <p className="text-sm text-red-400">
            Unable to load membership data right now.
          </p>
        ) : loadingLevels ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, idx) => (
              <div
                key={`benefit-skel-${idx}`}
                className="h-10 rounded-md bg-white/5 animate-pulse"
              />
            ))}
          </div>
        ) : benefitGroups.length ? (
          <div className="flex flex-col gap-6">
            {benefitGroups.map((group) => (
              <div key={group.title} className="flex flex-col gap-3">
                <h2 className="text-base font-semibold">{group.title}</h2>
                <div className="flex flex-col gap-2">
                  {group.items.map((item) => (
                    <div key={item.label} className="flex items-center gap-3">
                      <SolidIconBuilder
                        icon={Icons.Correct({ width: 20, height: 20 })}
                        color="#09B286"
                      />
                      <span className="text-sm md:text-base font-medium">
                        {item.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-white/60">No benefits available.</p>
        )}
      </div>
    </div>
  );
}
